import { useTranslate } from "ra-core";

import type { PublicQuote } from "../types";
import { formatKr, useIntervalLabel } from "./DealLinesEditor";
import { lineAmount } from "./dealLines";
import { isQuoteExpired, quoteTotals } from "./quotes";
import { useQuoteStatusLabel } from "./useQuoteStatusLabel";

const dateText = (key: string | null | undefined) => {
  if (!key) return "";
  const [y, m, d] = key.slice(0, 10).split("-");
  return `${Number(d)}.${Number(m)}.${y}`;
};

/**
 * Tilbudet slik kunden ser det. Brukes på kundelenken (og som PDF via
 * utskrift) og som forhåndsvisning i Mimin. Faste lyse farger, så tilbudet
 * ser likt ut på papir og i mørkt tema.
 */
export const QuoteDocument = ({ quote }: { quote: PublicQuote }) => {
  const translate = useTranslate();
  const intervalLabel = useIntervalLabel();
  const statusLabel = useQuoteStatusLabel();
  const totals = quoteTotals(quote.lines);
  const expired = isQuoteExpired(quote);
  const decided = quote.status === "accepted" || quote.status === "declined";

  return (
    <article className="quote-document mx-auto flex w-full max-w-[760px] flex-col gap-6 bg-white p-8 text-sm text-neutral-900">
      <header className="flex items-start justify-between gap-6">
        <div className="flex flex-col gap-2">
          {quote.logo ? (
            <img
              src={quote.logo}
              alt={quote.company_name ?? ""}
              className="h-10 w-auto self-start object-contain"
            />
          ) : null}
          {quote.company_name ? (
            <span className="font-medium">{quote.company_name}</span>
          ) : null}
        </div>
        <div className="flex flex-col items-end gap-1 text-right">
          <span className="text-xs tracking-wide text-neutral-500 uppercase">
            {translate("crm.quotes.document.label", { _: "Tilbud" })} #
            {quote.number}
          </span>
          {quote.sent_at ? (
            <span className="text-neutral-600">
              {translate("crm.quotes.document.date", { _: "Dato" })}{" "}
              {dateText(quote.sent_at)}
            </span>
          ) : null}
          {quote.valid_until ? (
            <span className={expired ? "text-red-700" : "text-neutral-600"}>
              {translate("crm.quotes.document.valid_until", {
                _: "Gyldig til",
              })}{" "}
              {dateText(quote.valid_until)}
            </span>
          ) : null}
          {decided || expired ? (
            <span
              className={
                quote.status === "accepted"
                  ? "rounded-full bg-emerald-50 px-2 py-0.5 text-xs text-emerald-800"
                  : "rounded-full bg-red-50 px-2 py-0.5 text-xs text-red-800"
              }
            >
              {expired
                ? translate("crm.quotes.expired", { _: "Utløpt" })
                : statusLabel(quote.status)}
            </span>
          ) : null}
        </div>
      </header>

      <div className="flex flex-col gap-1">
        {quote.customer ? (
          <span className="text-neutral-600">
            {translate("crm.quotes.document.to", { _: "Til" })} {quote.customer}
          </span>
        ) : null}
        <h1 className="text-2xl font-semibold">{quote.title || quote.deal}</h1>
      </div>

      {quote.intro ? (
        <p className="whitespace-pre-line leading-relaxed">{quote.intro}</p>
      ) : null}

      <table className="w-full border-collapse">
        <thead>
          <tr className="border-b border-neutral-300 text-left text-xs text-neutral-500">
            <th className="py-2 pr-2 font-normal">
              {translate("crm.quotes.document.description", {
                _: "Beskrivelse",
              })}
            </th>
            <th className="py-2 pr-2 text-right font-normal">
              {translate("crm.quotes.document.quantity", { _: "Antall" })}
            </th>
            <th className="py-2 pr-2 text-right font-normal">
              {translate("crm.quotes.document.unit_price", { _: "Pris" })}
            </th>
            <th className="py-2 pr-2 font-normal">
              {translate("crm.quotes.document.interval", { _: "Periode" })}
            </th>
            <th className="py-2 text-right font-normal">
              {translate("crm.quotes.document.amount", { _: "Sum" })}
            </th>
          </tr>
        </thead>
        <tbody>
          {quote.lines.map((line, index) => (
            <tr key={index} className="border-b border-neutral-200 align-top">
              <td className="py-2 pr-2">{line.description || "—"}</td>
              <td className="py-2 pr-2 text-right tabular-nums">
                {formatKr(Number(line.quantity))}
              </td>
              <td className="py-2 pr-2 text-right tabular-nums">
                {formatKr(Number(line.unit_price))}
              </td>
              <td className="py-2 pr-2 text-neutral-600">
                {intervalLabel(line.interval)}
              </td>
              <td className="py-2 text-right tabular-nums">
                {formatKr(lineAmount(line))} kr
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <dl className="ml-auto grid w-full max-w-xs grid-cols-[1fr_auto] gap-x-6 gap-y-1">
        {totals.oneTime > 0 && (
          <>
            <dt className="text-neutral-600">
              {translate("crm.quotes.totals.one_time", { _: "Engangs" })}
            </dt>
            <dd className="text-right tabular-nums">
              {formatKr(totals.oneTime)} kr
            </dd>
          </>
        )}
        {totals.monthly > 0 && (
          <>
            <dt className="text-neutral-600">
              {translate("crm.quotes.totals.monthly", { _: "Per måned" })}
            </dt>
            <dd className="text-right tabular-nums">
              {formatKr(totals.monthly)} kr
            </dd>
          </>
        )}
        <dt className="border-t border-neutral-300 pt-1 font-medium">
          {translate("crm.quotes.totals.first_year", { _: "Første år" })}
        </dt>
        <dd className="border-t border-neutral-300 pt-1 text-right font-medium tabular-nums">
          {formatKr(totals.firstYear)} kr
        </dd>
      </dl>
      <p className="text-xs text-neutral-500">
        {translate("crm.quotes.document.vat", {
          _: "Alle priser er oppgitt eks. mva.",
        })}
      </p>

      {quote.terms ? (
        <section className="flex flex-col gap-1">
          <h2 className="text-xs tracking-wide text-neutral-500 uppercase">
            {translate("crm.quotes.document.terms", { _: "Vilkår" })}
          </h2>
          <p className="whitespace-pre-line leading-relaxed">{quote.terms}</p>
        </section>
      ) : null}

      {quote.seller_name || quote.seller_email ? (
        <footer className="flex flex-col gap-0.5 border-t border-neutral-200 pt-4 text-neutral-600">
          <span>
            {translate("crm.quotes.document.contact", {
              _: "Spørsmål om tilbudet? Ta kontakt med",
            })}
          </span>
          <span className="text-neutral-900">
            {[quote.seller_name, quote.seller_email]
              .filter(Boolean)
              .join(" · ")}
          </span>
        </footer>
      ) : null}
    </article>
  );
};
